import React, { useCallback, useState } from "react";
import { DarwinAgentType, DarwinHeader } from "build-ts/common";
import { AgentInputProto, ChatContext, ChatRequest } from "build-ts/chat_session";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { refreshSystemPrompt } from "services/AgentChatService";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";
import { useChatContext } from "context/ChatContext";
import MessageInputAddMenu from "../components/MessageInputAddMenu";

interface MessageInputAddMenuProps {
  sessionHeader: DarwinHeader | undefined;
  analyst: Header | undefined;
  chatId: string;
  scenarioJobs?: ScheduledJobProto[];
  onSelectAnalysis?: (message: string) => void;
  onCsvFileSelected?: (files: File[]) => void;
}

const MessageInputAddMenuContainer: React.FC<MessageInputAddMenuProps> = ({
  sessionHeader,
  analyst,
  chatId,
  scenarioJobs,
  onSelectAnalysis,
  onCsvFileSelected,
}) => {
  const { getOrFetchAnalyst } = useChatContext();
  const [jobs, setJobs] = useState<ScheduledJobProto[]>([]);
  const [isFetchingJobs, setIsFetchingJobs] = useState(false);

  const fetchJobs = useCallback(async (): Promise<ScheduledJobProto[]> => {
    if (!analyst?.id) return [];
    // Use already-loaded scenario jobs if available — no conversion needed.
    if (scenarioJobs && scenarioJobs.length > 0) {
      setJobs(scenarioJobs);
      return scenarioJobs;
    }
    setIsFetchingJobs(true);
    const response = await listScheduledJobsByOwnerId(analyst?.id!, true);
    setIsFetchingJobs(false);
    const fetched = response ?? [];
    setJobs(fetched.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
    return fetched;
  }, [analyst?.id, scenarioJobs]);

  const handleLearnModalClose = useCallback(async () => {
    if (!analyst?.id || !sessionHeader?.id) return;
    const result = await getOrFetchAnalyst(analyst.id);
    if (!result) return;
    const chatContext = ChatContext.create({
      agent_type: DarwinAgentType.ANALYST_CHAT_AGENT,
      agent_input: AgentInputProto.create({
        analyst: result,
        scenario_jobs: scenarioJobs ?? [],
      }),
    });
    const chatRequest: ChatRequest = ChatRequest.create({
      session_header: sessionHeader,
      chat_context: chatContext,
      is_save: true,
    });
    await refreshSystemPrompt(chatRequest);
  }, [analyst?.id, sessionHeader, scenarioJobs, getOrFetchAnalyst]);

  return (
    <MessageInputAddMenu
      sessionHeader={sessionHeader}
      analyst={analyst}
      chatId={chatId}
      onSelectAnalysis={onSelectAnalysis}
      // Container-managed state
      jobs={jobs}
      isFetchingJobs={isFetchingJobs}
      // Container-provided callbacks
      onFetchJobs={fetchJobs}
      // Learn modal close handler - refreshes the system prompt
      onLearnModalClose={handleLearnModalClose}
      onCsvFileSelected={onCsvFileSelected}
    />
  );
};

export default MessageInputAddMenuContainer;
